import type { ReactNode } from "react";
import { cn } from "./cn.js";

type Props = {
  /** Small label shown above the value */
  label?: ReactNode;
  /** Main value (large display number, count, etc.) */
  value: ReactNode;
  /** Optional supplementary line under the value */
  hint?: ReactNode;
  /** Accent color override — useful for live indicators */
  tone?: "default" | "accent" | "danger" | "success" | "warning";
  align?: "left" | "center" | "right";
  className?: string;
};

const toneColor: Record<NonNullable<Props["tone"]>, string> = {
  default: "text-ink",
  accent: "text-accent-strong",
  danger: "text-danger-strong",
  success: "text-success",
  warning: "text-warning",
};

/** Big tabular-number display, used for score / clock / counts. */
export function Stat({
  label,
  value,
  hint,
  tone = "default",
  align = "left",
  className,
}: Props) {
  const alignClass =
    align === "center" ? "items-center text-center"
    : align === "right" ? "items-end text-right"
    : "items-start text-left";

  return (
    <div className={cn("flex flex-col gap-1", alignClass, className)}>
      {label && (
        <span className="text-[11px] uppercase tracking-[0.08em] text-ink-muted font-medium">
          {label}
        </span>
      )}
      <span
        className={cn(
          "font-display text-[44px] leading-none tabular-nums tracking-[-0.02em]",
          toneColor[tone],
        )}
      >
        {value}
      </span>
      {hint && <span className="text-[12px] text-ink-muted">{hint}</span>}
    </div>
  );
}
